import { Visibility_Enum } from '../gql'

// Why a proposal can be refused on a tension. Named rather than boolean: two
// causes coexist and they call for two different messages, one of which tells
// the reader to do something the other would make pointless.
export type ProposeRefusal = 'roleLevel' | 'noRole'

export interface ProposeVerdict {
  canPropose: boolean
  refusal?: ProposeRefusal
}

// Minimal shape needed from a thread, so the rule can be tested without loading
// one: only its visibility level matters here.
interface ThreadVisibility {
  visibility: Visibility_Enum
}

// Whether a proposal may be attached to a tension (GOUV-16, 14/08/2026).
//
// A tension at the Role level cannot carry one (it was « private » before
// CONF-02). A proposal is decided by the circle that CONTAINS the object
// (`getProposalVoterCircleId`, D6), while a Role-level tension is only readable
// by its own holders: the voters are not the readers. Worse, those unreachable
// voters still count in the denominator (`resolveProposal(…,
// voterUserIds.length)`), so an absolute majority could never be reached.
// At the Circle level the deciding circle IS a reader (4th round of CONF-02).
//
// The rule keys on the level alone, not on whether the voters actually
// happen to be readers (D1). A proposal whose voters scope is 'thread' would be
// sound — its voters are the tension's own participants — but that case only
// exists in Agile governance, and a rule nobody can predict costs more than the
// use it preserves. In Strict governance the scope is forced to 'circle', so
// the two readings coincide anyway.
//
// No thread means no level to weigh (D11): the hook is also called from a
// role's Security menu, where no tension is involved at all, and a rule firing
// there would break an unrelated path.
//
// `roleLevel` wins over `noRole` on purpose: the level blocks EVERYONE,
// whatever role they hold, so naming the role would send the reader looking for
// something that would not unblock them.
export function canProposeOnThread(
  thread: ThreadVisibility | null | undefined,
  holdsDecidingRole: boolean
): ProposeVerdict {
  if (thread?.visibility === Visibility_Enum.Role) {
    return { canPropose: false, refusal: 'roleLevel' }
  }
  if (!holdsDecidingRole) {
    return { canPropose: false, refusal: 'noRole' }
  }
  return { canPropose: true }
}
